import React from 'react';
import { ChartConfig } from '../../types';
import { FieldPicker } from './FieldPicker';
import { Target, TrendingUp, Tag, Grid, Clock } from 'lucide-react';

interface KPIMappingZoneProps {
    config: ChartConfig;
    onUpdateConfig: (updates: Partial<ChartConfig>) => void;
    allCols: string[];
    numericCols: string[];
    categoricalCols: string[];
    dateCols?: string[];
}

// Convert string arrays to FieldPicker option format
const toFieldOptions = (cols: string[], isNumeric: boolean = false) =>
    cols.map(col => ({ label: col, value: col, isNumeric }));

/**
 * KPI Card Field Mapping Zone
 * Provides field pickers for the KPI Card visualization
 */
export const KPIMappingZone: React.FC<KPIMappingZoneProps> = ({
    config,
    onUpdateConfig,
    allCols,
    numericCols,
    categoricalCols,
    dateCols = []
}) => {
    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center gap-2 text-neon-gold/80">
                <Target className="w-4 h-4" />
                <h3 className="text-xs font-bold uppercase tracking-widest">KPI Card Fields</h3>
            </div>

            <div className="space-y-3">
                {/* Value Field - Required */}
                <div className="space-y-1">
                    <FieldPicker
                        label="Value Field"
                        value={config.kpiValueField}
                        onChange={(val) => onUpdateConfig({ kpiValueField: val })}
                        options={toFieldOptions(numericCols, true)}
                    />
                    <p className="text-[10px] text-gray-500 pl-1">The main KPI value to display (required)</p>
                </div>

                {/* Comparison Field - Optional */}
                <div className="space-y-1">
                    <FieldPicker
                        label="Comparison Field"
                        value={config.kpiComparisonField}
                        onChange={(val) => onUpdateConfig({ kpiComparisonField: val || undefined })}
                        options={toFieldOptions(numericCols, true)}
                        allowNone
                    />
                    <p className="text-[10px] text-gray-500 pl-1">Previous period value for delta calculation</p>
                </div>
            </div>

            <div className="h-px bg-white/10 w-full" />

            {/* Advanced Mappings */}
            <div className="space-y-3">
                <h4 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                    <Grid className="w-3 h-3" /> Advanced
                </h4>

                {/* Group By - Optional */}
                <div className="space-y-1">
                    <FieldPicker
                        label="Group By"
                        value={config.kpiGroupByField}
                        onChange={(val) => onUpdateConfig({ kpiGroupByField: val || undefined })}
                        options={toFieldOptions(categoricalCols)}
                        allowNone
                    />
                    <p className="text-[10px] text-gray-500 pl-1">Create one KPI card per group</p>
                </div>

                {/* Time Field - Optional (for sparkline) */}
                <div className="space-y-1">
                    <FieldPicker
                        label="Time Field"
                        value={config.kpiTimeField}
                        onChange={(val) => onUpdateConfig({ kpiTimeField: val || undefined })}
                        options={toFieldOptions(dateCols.length > 0 ? dateCols : allCols)}
                        allowNone
                    />
                    <p className="text-[10px] text-gray-500 pl-1">For sparkline trend (requires date/time column)</p>
                </div>
            </div>
        </div>
    );
};

export default KPIMappingZone;
